import RequestService from "./RequestService";
import { Assistant } from "../entities/company/Assistant/Assistant";
import CompanyService from "./CompanyService";
import AuthenticationService from "./AuthenticationService";
import ErrorLoggingService from "./ErrorLoggingService";
import {
  AssistantChatRequest,
  AssistantChatStreamHandlers,
  AssistantConversation,
  AssistantConversationMessage
} from "../entities/company/Assistant/AssistantChat";
import {
  AUTHENTICATION_DATA_STORAGE_KEY,
  IAuthenticationData
} from "../pages/shared/AuthenticationContext";
import { getCache } from "../utils/PersistenceUtils";
import { parseSseBuffer } from "./assistant/sseParser";

class AssistantService {
  public static async aicategorize(
    id: number,
    full?: string
  ): Promise<any | Error> {
    return RequestService.post(
      "/assistant/aicategorize",
      { id, full },
      true,
      RequestService.getBaseUrlAssistance(),
      60000
    );
  }

  public static async setAICategorized(
    personid: number,
    data: any,
    messageid: number
  ): Promise<any | Error> {
    return RequestService.post("/assistant/setaicategorized", {
      personid,
      data: JSON.stringify(data),
      messageid,
      additionaldefinitions: CompanyService.getAdditionalDefinitions(),
      iostatus: CompanyService.getIoStatus()
    });
  }

  public static async getInstructions(): Promise<Assistant | Error> {
    return RequestService.post("/assistant/get", {});
  }

  public static async setInstructions(
    assistant: Assistant
  ): Promise<any | Error> {
    return RequestService.post("/assistant/set", { item: assistant });
  }

  /** Últimas conversaciones del usuario del token (GDC-5449). */
  public static async getConversations(
    take: number = 10
  ): Promise<AssistantConversation[] | Error> {
    return RequestService.get<AssistantConversation[]>(
      `/assistant/conversations?take=${take}`,
      {},
      true,
      RequestService.getBaseUrlAssistance()
    );
  }

  /** Mensajes de una conversación, ordenados por `seq` (GDC-5449). */
  public static async getConversationMessages(
    conversationId: number
  ): Promise<AssistantConversationMessage[] | Error> {
    return RequestService.get<AssistantConversationMessage[]>(
      `/assistant/conversations/${conversationId}/messages`,
      {},
      true,
      RequestService.getBaseUrlAssistance()
    );
  }

  /**
   * Envía un turno al asistente por `POST /assistant/chat` (SSE).
   * Usa fetch + ReadableStream: EventSource no soporta POST y axios no hace streaming.
   * Nunca rechaza: los errores se informan por `handlers.onError`.
   */
  public static async sendChatStreaming(
    request: AssistantChatRequest,
    handlers: AssistantChatStreamHandlers,
    signal?: AbortSignal
  ): Promise<void> {
    const url = RequestService.getBaseUrlAssistance() + "/assistant/chat";
    const body = JSON.stringify({
      message: request.message,
      conversationId: request.conversationId ?? null
    });
    const doFetch = (token: string) =>
      fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token
        },
        body,
        signal
      });

    try {
      const token = await AuthenticationService.getAuthenticationToken();
      if (!token) {
        handlers.onError("unauthorized");
        return;
      }

      let response = await doFetch(token);

      // 401: refrescar token y reintentar una vez (mismo criterio que RequestService)
      if (response.status === 401) {
        const authenticationData = getCache<IAuthenticationData>(
          AUTHENTICATION_DATA_STORAGE_KEY
        );
        const refreshedToken = authenticationData
          ? await AuthenticationService.refreshToken({
              refreshToken: authenticationData
            })
          : null;
        if (!refreshedToken) {
          handlers.onError("unauthorized");
          return;
        }
        response = await doFetch(refreshedToken);
      }

      if (!response.ok || !response.body) {
        if (response.status === 401) {
          handlers.onError("unauthorized");
        } else if (response.status === 402) {
          handlers.onError("insufficient_balance");
        } else {
          ErrorLoggingService.logError(
            `Assistant chat request failed with status ${response.status}`,
            url,
            { conversationId: request.conversationId }
          );
          handlers.onError("generic");
        }
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let finished = false;

      const dispatch = (chunk: string) => {
        const { events, rest } = parseSseBuffer(chunk);
        for (const event of events) {
          if (finished) {
            break;
          }
          switch (event.type) {
            case "conversation":
              handlers.onConversationId(event.conversationId);
              break;
            case "tool":
              handlers.onTool(event.tool);
              break;
            case "text":
              handlers.onTextDelta(event.text);
              break;
            case "done":
              finished = true;
              handlers.onDone();
              break;
            case "error":
              finished = true;
              handlers.onError("generic", event.message);
              break;
          }
        }
        return rest;
      };

      while (!finished) {
        const { value, done } = await reader.read();
        if (done) {
          break;
        }
        buffer = dispatch(buffer + decoder.decode(value, { stream: true }));
      }

      if (!finished) {
        // El stream cerró sin evento `done`: procesar lo que quedó y cerrar el turno.
        dispatch(buffer + decoder.decode() + "\n\n");
        if (!finished) {
          handlers.onDone();
        }
      } else {
        reader.cancel().catch(() => undefined);
      }
    } catch (error) {
      if (signal?.aborted || (error as Error)?.name === "AbortError") {
        return;
      }
      ErrorLoggingService.logError(error as Error, url, {
        conversationId: request.conversationId
      });
      handlers.onError("generic");
    }
  }
}

export default AssistantService;
